import React, {
    ElementType,
    FocusEventHandler,
    memo,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { TooltipRoot, TooltipBlock, TooltipInlineBlock } from './tooltip.styles';
import { Text, TextType } from '../text';
import { isBrowser } from '../../utils/env';
import { useIsomorphicLayoutEffect } from '../../utils/hooks';

interface TooltipProps {
    children: React.ReactNode;
    positioningElement?: HTMLElement | null;
    $show?: boolean;
    id?: string;
}

function Tooltip({ children, positioningElement, $show, id }: TooltipProps) {
    const theme = useTheme();

    // null means “not measured yet”
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const rafRef = useRef<number | null>(null);

    // Synchronous first measurement before paint
    useIsomorphicLayoutEffect(() => {
        if (!$show || !positioningElement) {
            setPos(null);
            return;
        }

        const measureNow = () => {
            const r = positioningElement.getBoundingClientRect();
            const gap = Number(theme.spacingRaw?.(1) ?? 8);
            const vw = document.documentElement.clientWidth;

            const centerX = Math.round(r.left + r.width / 2);
            const y = Math.round(r.bottom + gap);
            const x = Math.min(vw - 8, Math.max(8, centerX));

            setPos({ x, y });
        };

        measureNow(); // runs before paint
    }, [$show, positioningElement, theme]);

    // Live updates while visible (scroll/resize)
    useEffect(() => {
        if (!$show || !positioningElement) return;

        const schedule = () => {
            if (rafRef.current != null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;

                const r = positioningElement.getBoundingClientRect();
                const gap = Number(theme.spacingRaw?.(1) ?? 8);
                const vw = document.documentElement.clientWidth;

                const centerX = Math.round(r.left + r.width / 2);
                const y = Math.round(r.bottom + gap);
                const x = Math.min(vw - 8, Math.max(8, centerX));

                setPos((p) => (p && p.x === x && p.y === y ? p : { x, y }));
            });
        };

        const opts: AddEventListenerOptions = { passive: true };
        window.addEventListener('scroll', schedule, opts);
        window.addEventListener('resize', schedule);
        schedule();

        return () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            window.removeEventListener('scroll', schedule, opts);
            window.removeEventListener('resize', schedule);
        };
    }, [$show, positioningElement, theme]);

    // Don’t render until we have a measured position, so there’s no first-frame jump.
    if (!isBrowser || !$show || pos == null) return null;

    return createPortal(
        <TooltipRoot $position={pos} $show role="tooltip" id={id}>
            <Text type={TextType.descriptionSmall} color={theme.tooltipColor}>
                {children}
            </Text>
        </TooltipRoot>,
        document.body
    );
}

interface WithTooltipProps {
    text?: ReactNode;
    children: ReactNode;
    inline?: boolean;
}

function TooltipWrapper({ text, children, inline = false }: WithTooltipProps) {
    const [visible, setVisible] = React.useState(false);
    const anchorRef = useRef<HTMLElement | null>(null);
    const tooltipId = useId();
    const Container: ElementType = inline ? TooltipInlineBlock : TooltipBlock;

    if (!text) return <>{children}</>;

    const onEnter: MouseEventHandler<HTMLElement> = (e) => {
        anchorRef.current = e.currentTarget as HTMLElement;
        setVisible(true);
    };
    const onLeave = () => setVisible(false);
    const onFocus: FocusEventHandler<HTMLElement> = (e) => {
        anchorRef.current = e.currentTarget as HTMLElement;
        setVisible(true);
    };
    const onBlur = () => setVisible(false);

    return (
        <Container
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-describedby={visible ? tooltipId : undefined}
        >
            {children}
            <Tooltip $show={visible} positioningElement={anchorRef.current} id={tooltipId}>
                {text}
            </Tooltip>
        </Container>
    );
}

export const WithTooltip = memo(TooltipWrapper);
